/**
 * usePriChat — API hook for pRI chat communication.
 * POST /api/v1/pri/chat     — send message
 * POST /api/v1/pri/context   — quick action (how_to / what_is)
 * GET  /api/v1/pri/health    — health check
 *
 * S-PRI-2.5/2.6: Enhanced sendQuickAction with auto-detected route context.
 * Uses the centralized ApiClient (api.post / api.get) for auth + token refresh.
 */
import { api, ApiError } from '@/api/client';
import { usePriStore } from '@/stores/pri-store';
import { useCallback, useRef } from 'react';

interface PriChatResponse {
  message: string;
  sessionId?: string;
  intent?: string;
  tier?: string;
}

interface PriContextResponse {
  message: string;
  content?: string;
  intent?: string;
  tier?: string;
  relatedPages?: Array<{ label: string; path: string }>;
  usedKbArticleId?: string;
}

interface PriHealthResponse {
  status: string;
}

interface PriChatOptions {
  context?: { module: string; page: string };
}

const ERROR_FALLBACK = 'Sajnalom, jelenleg nem tudok valaszolni. Probald ujra kesobb.';

export function usePriChat() {
  const addMessage = usePriStore(s => s.addMessage);
  const setLoading = usePriStore(s => s.setLoading);
  const setSessionId = usePriStore(s => s.setSessionId);

  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (message: string, options?: PriChatOptions) => {
      const trimmed = message.trim();
      if (!trimmed) return;

      // 1. Add user message to store
      addMessage({ role: 'user', content: trimmed });

      // 2. Set loading
      setLoading(true);

      // 3. Abort previous request if any
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        // 4. POST /api/v1/pri/chat
        const currentSessionId = usePriStore.getState().sessionId;
        const response = await api.post<PriChatResponse>(
          '/pri/chat',
          {
            message: trimmed,
            sessionId: currentSessionId,
            context: options?.context,
          },
          { signal: controller.signal }
        );

        // 5. Update session ID if returned
        if (response.sessionId) {
          setSessionId(response.sessionId);
        }

        // 6. Add assistant message to store
        addMessage({
          role: 'assistant',
          content: response.message,
          intent: response.intent,
          tier: response.tier,
        });
      } catch (error) {
        // Don't show error for aborted requests
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        const errorMessage = error instanceof ApiError ? error.message : ERROR_FALLBACK;

        addMessage({
          role: 'assistant',
          content: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    },
    [addMessage, setLoading, setSessionId]
  );

  /**
   * Send a quick action (how_to or what_is) with module + page context.
   * S-PRI-2.5/2.6: Builds full PriContext for the backend.
   */
  const sendQuickAction = useCallback(
    async (type: 'how_to' | 'what_is', module: string, page: string) => {
      // Add user message representing the quick action
      const label = type === 'how_to' ? `Hogyan kell...? (${page})` : `Mi ez az oldal? (${page})`;
      addMessage({ role: 'user', content: label });

      setLoading(true);

      try {
        // Build full context for the backend (S-PRI-2.5/2.6)
        const routePath = typeof window !== 'undefined' ? window.location.pathname : '/';

        const response = await api.post<PriContextResponse>('/pri/context', {
          type,
          context: {
            page: {
              module: module.toLowerCase(),
              page,
              routePath,
            },
            user: {
              userId: '00000000-0000-0000-0000-000000000001',
              userName: 'User',
              tenantId: '00000000-0000-0000-0000-000000000001',
              role: 'BOLTVEZETO',
              permissions: [],
            },
            timestamp: new Date().toISOString(),
            sessionId: usePriStore.getState().sessionId ?? `pri-${Date.now()}`,
          },
        });

        // The backend returns content in the `content` field, or `message` as fallback
        const content = response.content ?? response.message;

        addMessage({
          role: 'assistant',
          content,
          intent: response.intent,
          tier: response.tier,
        });
      } catch (error) {
        const errorMessage = error instanceof ApiError ? error.message : ERROR_FALLBACK;

        addMessage({
          role: 'assistant',
          content: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    },
    [addMessage, setLoading]
  );

  const checkHealth = useCallback(async (): Promise<boolean> => {
    try {
      const response = await api.get<PriHealthResponse>('/pri/health');
      return response.status === 'ok';
    } catch {
      return false;
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { sendMessage, sendQuickAction, checkHealth, cancel };
}
